/**
 * Pure maths of the wind overlay: how many particles, the screen-space wind lookup, one advection
 * step. No DOM, no Leaflet; the layer feeds it a way to ask "wind at this pixel".
 *
 * Particles move in SCREEN space (u east = +x, v north = −y) at a fixed number of pixels per second
 * per m/s of wind, so the picture looks the same at every zoom. A pixel without wind data (outside
 * the grid, or a missing grid corner) yields `null`: such a particle is respawned, never drawn.
 */

export type Rng = () => number;
export type WindUV = { u: number; v: number };
/** Wind at a pixel, in m/s: u east, v north. Null = no data. */
export type PixelSampler = (x: number, y: number) => WindUV | null;

/** Screen pixels a particle travels per second for each m/s of wind. */
export const PX_PER_MS = 6;
/** Particle lifetime range in seconds. */
export const TTL_MIN_S = 1.5;
export const TTL_MAX_S = 4;
/** One particle per this many CSS pixels² of viewport. */
export const PIXELS_PER_PARTICLE = 2000;
export const MAX_PARTICLES_DESKTOP = 2500;
export const MAX_PARTICLES_PHONE = 1000;
export const MAX_DPR = 2;

export function particleCount(width: number, height: number, phone: boolean): number {
  const n = Math.round((width * height) / PIXELS_PER_PARTICLE);
  return Math.max(0, Math.min(n, phone ? MAX_PARTICLES_PHONE : MAX_PARTICLES_DESKTOP));
}

export const canvasDpr = (devicePixelRatio: number): number =>
  Math.max(1, Math.min(devicePixelRatio || 1, MAX_DPR));

/** Wind (m/s) → pixel velocity (px/s); screen y grows downwards, so north is −y. */
export const pixelVelocity = (w: WindUV): { vx: number; vy: number } => ({
  vx: w.u * PX_PER_MS,
  vy: -w.v * PX_PER_MS,
});

/**
 * Wind on a regular pixel grid (`cell` px apart, covering width × height), filled once per view /
 * hour so the per-frame loop is a bilinear lookup instead of a map projection per particle.
 * A cell is null when the wind is unknown there; a lookup touching a null cell is null.
 */
export interface ScreenField {
  cell: number;
  nx: number;
  ny: number;
  u: Float32Array;
  v: Float32Array;
  ok: Uint8Array;
}

export function buildScreenField(
  width: number,
  height: number,
  cell: number,
  sample: PixelSampler,
): ScreenField {
  const nx = Math.ceil(width / cell) + 1;
  const ny = Math.ceil(height / cell) + 1;
  const f: ScreenField = {
    cell,
    nx,
    ny,
    u: new Float32Array(nx * ny),
    v: new Float32Array(nx * ny),
    ok: new Uint8Array(nx * ny),
  };
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      const w = sample(i * cell, j * cell);
      const k = j * nx + i;
      if (w) {
        f.u[k] = w.u;
        f.v[k] = w.v;
        f.ok[k] = 1;
      }
    }
  return f;
}

export function lookup(f: ScreenField, x: number, y: number): WindUV | null {
  const gx = x / f.cell;
  const gy = y / f.cell;
  if (!(gx >= 0 && gy >= 0 && gx <= f.nx - 1 && gy <= f.ny - 1)) return null;
  const i0 = Math.min(Math.floor(gx), f.nx - 2);
  const j0 = Math.min(Math.floor(gy), f.ny - 2);
  const fx = gx - i0;
  const fy = gy - j0;
  const k00 = j0 * f.nx + i0;
  const k10 = k00 + 1;
  const k01 = k00 + f.nx;
  const k11 = k01 + 1;
  if (!(f.ok[k00] && f.ok[k10] && f.ok[k01] && f.ok[k11])) return null;
  const w00 = (1 - fx) * (1 - fy);
  const w10 = fx * (1 - fy);
  const w01 = (1 - fx) * fy;
  const w11 = fx * fy;
  return {
    u: f.u[k00]! * w00 + f.u[k10]! * w10 + f.u[k01]! * w01 + f.u[k11]! * w11,
    v: f.v[k00]! * w00 + f.v[k10]! * w10 + f.v[k01]! * w01 + f.v[k11]! * w11,
  };
}

export interface Particles {
  n: number;
  x: Float32Array;
  y: Float32Array;
  /** Position before the last step: a trail segment is drawn from here. */
  px: Float32Array;
  py: Float32Array;
  age: Float32Array;
  ttl: Float32Array;
  /** 1 when the last step moved the particle over known wind (draw its segment). */
  live: Uint8Array;
}

export function createParticles(n: number, width: number, height: number, rng: Rng): Particles {
  const p: Particles = {
    n,
    x: new Float32Array(n),
    y: new Float32Array(n),
    px: new Float32Array(n),
    py: new Float32Array(n),
    age: new Float32Array(n),
    ttl: new Float32Array(n),
    live: new Uint8Array(n),
  };
  for (let i = 0; i < n; i++) respawn(p, i, width, height, rng, true);
  return p;
}

/** Puts particle `i` at a random pixel; `scatterAge` spreads first-time ages so they do not die together. */
export function respawn(
  p: Particles,
  i: number,
  width: number,
  height: number,
  rng: Rng,
  scatterAge = false,
): void {
  const x = rng() * width;
  const y = rng() * height;
  p.x[i] = x;
  p.y[i] = y;
  p.px[i] = x;
  p.py[i] = y;
  p.ttl[i] = TTL_MIN_S + rng() * (TTL_MAX_S - TTL_MIN_S);
  p.age[i] = scatterAge ? rng() * p.ttl[i]! : 0;
  p.live[i] = 0;
}

/**
 * Advances every particle by `dt` seconds. A particle that is too old, leaves the viewport or sits
 * where there is no wind data is respawned (and not drawn this step).
 */
export function stepParticles(
  p: Particles,
  dt: number,
  width: number,
  height: number,
  field: ScreenField,
  rng: Rng,
): void {
  for (let i = 0; i < p.n; i++) {
    p.age[i]! += dt;
    const w = lookup(field, p.x[i]!, p.y[i]!);
    if (!w || p.age[i]! > p.ttl[i]!) {
      respawn(p, i, width, height, rng);
      continue;
    }
    const { vx, vy } = pixelVelocity(w);
    p.px[i] = p.x[i]!;
    p.py[i] = p.y[i]!;
    p.x[i] = p.x[i]! + vx * dt;
    p.y[i] = p.y[i]! + vy * dt;
    if (p.x[i]! < 0 || p.y[i]! < 0 || p.x[i]! > width || p.y[i]! > height) {
      respawn(p, i, width, height, rng);
      continue;
    }
    p.live[i] = 1;
  }
}
